'use client';

import { useTranslation } from '@/hooks/useTranslation';
import Link from 'next/link';
import { IntegrationChatbot } from '@/constants/IntegrationChatbot';
import Image from 'next/image';
import ReactHtmlParser from 'react-html-parser';
import CTABanner from '@/components/blocks/CTABanner';
import { BsTools } from 'react-icons/bs';
import { BiSolidPlusCircle } from 'react-icons/bi';
import React from 'react';

interface PageContentProps {
  locale: string;
}

export default function PageContent({ locale }: PageContentProps) {
  const { t } = useTranslation(locale);

  return (
    <div>
      <div className="tw-flex tw-flex-col">
        <div className="tw-flex tw-flex-col tw-p-6 tw-gap-6">
          <h1 className="tw-text-2xl tw-text-center">{t('IntegrationsPage/header')}</h1>

          <div className="tw-grid xl:tw-grid-cols-3 tw-grid-cols-1  tw-gap-3">
            {IntegrationChatbot.map((item) => (
              <Link
                href={`/${locale}/chatbot-integration/${item.name}`}
                key={item.name}
                className="md:tw-w-full tw-w-full tw-min-h-72  tw-bg-white tw-rounded-lg tw-shadow tw-border-4 tw-border-solid hover:tw-cursor-pointer tw-border-transparent hover:tw-border-blue-500 tw-flex-col tw-justify-between tw-items-start tw-inline-flex"
              >
                <div className="tw-flex tw-flex-col tw-p-4 tw-gap-4">
                  <div className="header tw-flex tw-gap-3">
                    <div>
                      <Image
                        priority
                        quality={100}
                        src={item.image}
                        alt={t(item.title)}
                        title={t(item.title)}
                        width={40}
                        height={40}
                      />
                    </div>
                    <div className="tw-flex tw-flex-col">
                      <div className=" tw-text-gray-900 tw-text-lg tw-font-semibold font-sofiaSans tw-leading-7">
                        {t(item.title)}
                      </div>
                      <div className="tw-text-gray-500 tw-text-sm tw-font-normal font-sofiaSans tw-leading-tight">
                        {t(item.subTitle)}
                      </div>
                    </div>
                  </div>
                  <div className="difficulty tw-flex  tw-gap-1 tw-items-center">
                    <div className="Difficulty text-gray-500 text-sm font-normal font-sofiaSans leading-tight">
                      {t('IntegrationsPage/difficulty')}:
                    </div>
                    {new Array(item.difficulty).fill(null).map((item, index) => (
                      <BsTools key={index} />
                    ))}
                  </div>
                  <div className="body tw-text-gray-900 tw-text-sm tw-font-normal font-sofiaSans tw-leading-tight">
                    {t(item.describe)}
                  </div>
                  <div className="tw-flex tw-items-center tw-cursor-pointer">
                    <div className="tw-text-blue-700 tw-text-sm tw-font-normal font-sofiaSans tw-leading-tight">
                      {t('IntegrationsPage/try-button')}
                    </div>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15.8333 10L10 5V9.16667H5V10.8333H10V15L15.8333 10Z" fill="#1D4ED8" />
                    </svg>
                  </div>
                </div>
              </Link>
            ))}

            <Link
              href={`/${locale}/chatbot-integration/gtm`}
              key="prestashop"
              className="md:tw-w-full tw-w-full tw-min-h-72  tw-bg-white tw-rounded-lg tw-shadow tw-border-4 tw-border-solid hover:tw-cursor-pointer tw-border-transparent hover:tw-border-blue-500 tw-flex-col tw-justify-between tw-items-start tw-inline-flex"
            >
              <div className="tw-flex tw-flex-col tw-p-4 tw-gap-4">
                <div className="header tw-flex tw-gap-3">
                  <div>
                    <Image
                      priority
                      quality={100}
                      src="https://cdn.1cdn.app/application/LOUISEBOT/25041321205526_logo_prestashop.png"
                      alt="prestashop"
                      title="prestashop"
                      width={40}
                      height={40}
                    />
                  </div>
                  <div className="tw-flex tw-flex-col">
                    <div className=" tw-text-gray-900 tw-text-lg tw-font-semibold font-sofiaSans tw-leading-7">
                      {t('IntegrationsPage/Prestashop/title')}:
                    </div>
                    <div className="tw-text-gray-500 tw-text-sm tw-font-normal font-sofiaSans tw-leading-tight">
                      CMS
                    </div>
                  </div>
                </div>
                <div className="difficulty tw-flex  tw-gap-1 tw-items-center">
                  <div className="Difficulty text-gray-500 text-sm font-normal font-sofiaSans leading-tight">
                    {t('IntegrationsPage/difficulty')}:
                  </div>
                  <BsTools />
                  <BsTools />
                  <BsTools />
                </div>
                <div className="body tw-text-gray-900 tw-text-sm tw-font-normal font-sofiaSans tw-leading-tight">
                  {ReactHtmlParser(t('IntegrationsPage/Prestashop/describe'))}
                </div>
                <div className="tw-flex tw-items-center tw-cursor-pointer">
                  <div className="tw-text-blue-700 tw-text-sm tw-font-normal font-sofiaSans tw-leading-tight">
                    {t('IntegrationsPage/Prestashop/cta-button')}
                  </div>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15.8333 10L10 5V9.16667H5V10.8333H10V15L15.8333 10Z" fill="#1D4ED8" />
                  </svg>
                </div>
              </div>
            </Link>

            <Link
              href={`/${locale}/contact-us`}
              className="md:tw-w-full tw-w-full tw-min-h-72 tw-h-64 tw-px-[45x] tw-pt-4 tw-pb-6 tw-bg-blue-600 tw-rounded-2xl  tw-flex-col tw-justify-center tw-items-center tw-gap-5 tw-inline-flex tw-shadow tw-border-4 tw-border-solid hover:tw-cursor-pointer tw-border-transparent hover:tw-border-blue-500"
            >
              <div className="tw-self-stretch tw-h-[120px] tw-flex tw-flex-col tw-justify-center tw-items-center tw-gap-2">
                <div className="tw-w-12 tw-h-12 tw-relative tw-mb-2  ">
                  <div className="tw-w-full tw-h-full tw-absolute tw-bg-blue-700 tw-rounded-lg " />
                  {
                    <BiSolidPlusCircle className="tw-w-6 tw-h-6 tw-absolute tw-left-1/2 tw-top-1/2 tw-transform tw--translate-x-1/2 tw--translate-y-1/2 tw-text-white " />
                  }
                </div>

                <div className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-h-16 tw-w-full">
                  <div className="tw-text-center tw-text-white tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7 tw-mb-1 ">
                    {t('IntegrationsPage/plateform/title')}
                  </div>
                  <div className="tw-text-left tw-text-blue-200 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-4 tw-px-8">
                    {t('IntegrationsPage/plateform/describe')}
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>

      <CTABanner
        title={t('Home/ReadyToGetStarted/cta-title')}
        description={t('Home/ReadyToGetStarted/cta-description')}
        cta={t('Home/ReadyToGetStarted/start-trial-aria')}
        ctaTarget="register"
        locale={locale}
      />
    </div>
  );
}
